import * as React from "react"
import {Amplify, Logger} from "aws-amplify"
import liff from "@line/liff"

import "./src/styles/global.css"
import {awsConfig} from "./src/libs/aws/config"
import BUILD_VERSION from "./BUILD_VERSION"
import {QueryProvider} from "./src/libs/query"
import {
  GATSBY_I18NEXT_KEY,
  BACKUP_GATSBY_I18NEXT_KEY,
  DEFAULT_LANGUAGE,
  SUPPORT_LANGUAGES,
  getLanguageFromLocation,
  isSupportedLanguage,
  normalizeLanguage,
} from "./src/libs/gatsby-i18next"
import {
  registerLineBinding,
  registerLoginRedirect,
  registerLiffInit,
  injectCognitoToken,
  clearLoginInfoByClientId,
  registerCallbackClient,
  getCallbackClient,
  hasCallbackClient,
} from "./src/services/auth/cache"
import {clearAmplifyOAuthState} from "./src/services/auth/sessionCache"
import {captureContactTicketSource} from "./src/features/contact-ticket"
import RootPageElement from "./src/components/layouts/RootPageElement"
import {HubHelper, Errors} from "./src/utils/awsHubHelper"
import {ERR_HUB_SIGN_IN_FAILURE} from "./src/constants/error"
import {determineTheme, applyTheme} from "./src/services/theme"
import {RecaptchaProviders} from "./src/components/shared/recaptcha"

const FALLBACK_LANGUAGE_PAGE_PATHS = new Set([
  "/",
  "/auth/callback/",
  "/auth/forgot/user/",
  "/auth/liff-google-signin/",
  "/auth/sign-in/",
  "/auth/sign-up/",
  "/auth/unlink/",
  "/barcode/",
  "/contact/",
  "/family/",
  "/family/member/",
  "/oauth/sign-in/",
  "/policy/",
  "/profile/",
  "/ridelife-policy/",
  "/security/",
])

// add language to path, only if language is not the default language
// so the aws auth could redirect to the correct path
// path example "http://localhost:8000/auth/sign-in/" (the one in the .env)
function rewritePathWithLanguage(path = "", language) {
  if (!language || language === DEFAULT_LANGUAGE) return path

  const url = new URL(path)
  return `${url.origin}/${language}${url.pathname}`
}

function redirectUnsupportedLanguagePathToDefault() {
  const {pathname, search, hash} = window.location
  const [, maybeLanguage, ...restPathSegments] = pathname.split("/")
  const fallbackPathSegments = restPathSegments.filter(Boolean)

  if (SUPPORT_LANGUAGES.length === 0) return false
  if (!maybeLanguage || isSupportedLanguage(maybeLanguage)) return false
  if (fallbackPathSegments.length === 0) return false

  const fallbackPath = `/${fallbackPathSegments.join("/")}/`
  if (!FALLBACK_LANGUAGE_PAGE_PATHS.has(fallbackPath)) return false

  window.location.replace(
    `/${DEFAULT_LANGUAGE}${fallbackPath}${search}${hash}`,
  )
  return true
}

/**
 *
 * @param {import("@aws-amplify/core").HubPayload} payload
 */
function handleSignInFailure(payload) {
  const {error} = HubHelper.parseSignInFailure(payload)

  function redirectWithOAuthError(oauthError) {
    window.location.href = rewritePathWithLanguage(
      `/auth/sign-in?oauthError=${oauthError}`,
    )
  }

  if (process.env.GATSBY_AMPLIFY_DEBUG) {
    console.debug(
      "Capture any signIn failure.",
      "Details:",
      JSON.stringify({
        href: window.location.href,
        qs: window.location.search,
        payload,
      }),
    )
  }

  switch (error) {
    case Errors.FB_USER_DENIED:
      // We don't need to take any action on this
      break
    case Errors.INVALID_OAUTH_STATE:
      /**
       * Just redirect user to Sign-In Page is fine.
       * The stale oauth_state in session storage will be automatically cleared by Amplify.
       */
      redirectWithOAuthError(ERR_HUB_SIGN_IN_FAILURE)
      break
    case Errors.UNKNOWN:
    default:
      console.debug(
        "Capture unhandled signIn failure.",
        "Details:",
        JSON.stringify({
          href: window.location.href,
          qs: window.location.search,
          message: payload.message,
          detail: payload.data?.toString(),
        }),
      )
      redirectWithOAuthError(ERR_HUB_SIGN_IN_FAILURE)
  }
}

/**
 * @param {{
 *  payload: import("@aws-amplify/core").HubPayload
 * }} param0
 */
function amplifyAuthEventHandler({payload}) {
  switch (payload.event) {
    case "signIn_failure":
      handleSignInFailure(payload)
      break
    default:
    // console.debug("Hub Payload:", JSON.stringify(payload))
  }
}

HubHelper.registerListener("auth", amplifyAuthEventHandler)

export const onClientEntry = () => {
  captureContactTicketSource(window.location.search)
  if (redirectUnsupportedLanguagePathToDefault()) return

  const currentPathname = window.location.pathname
  const searchParams = new URLSearchParams(window.location.search)
  const clientId = searchParams.get("client_id")
  const callbackUri = searchParams.get("callback_uri") // for client id redirect
  // for client id redirect, When client adds a callback_state search parameter to a request,
  // GID will redirect back with the same value to this search parameter "?state=". (along with other tokens)
  const callbackState = searchParams.get("callback_state")
  const loginRedirectUrl = searchParams.get("login_redirect")
  const liffInit = searchParams.get("liff.state")
  const lineBind = searchParams.get("line_bind")
  const idToken = searchParams.get("id_token")
  const refreshToken = searchParams.get("refresh_token")
  const accessToken = searchParams.get("access_token")
  const error = searchParams.get("error")
  const errorDescription = searchParams.get("error_description")

  /** Setting light/dark theme for website - BEGIN */
  const {theme} = determineTheme()
  applyTheme(theme)
  /** Setting light/dark theme for website - END */

  liff
    .init({
      liffId: process.env.GATSBY_LIFF_ID ?? "",
    })
    .catch(e => {
      /* Catch error so it won't crash if LIFF initiation failed */
      console.error("LIFF Init Failed", e)
    })

  // If the build version is different, items in local storage will be cleared (except language preference)
  // so that the user will be forced to login again
  if (
    process.env.NODE_ENV === "production" &&
    localStorage.getItem("build_version") !== process.env.BUILD_VERSION
  ) {
    clearLoginInfoByClientId(awsConfig.Auth.userPoolWebClientId)
    localStorage.removeItem("giant-id-user-store")
    localStorage.setItem("build_version", process.env.BUILD_VERSION)
  }

  if (loginRedirectUrl) {
    registerLoginRedirect(loginRedirectUrl)
  }

  /**
   * Register a callback client to redirect the user back to the client after login.
   * Alternatively, clear outdated callback client data when users come directly to
   * the website to prevent unexpected redirects.
   */
  if (
    currentPathname.includes("/oauth/sign-in") &&
    !!clientId &&
    !!callbackUri
  ) {
    registerCallbackClient(clientId, callbackUri, callbackState)
  }

  if (lineBind) {
    // line_bind is for user to close this in-app browser when logged in successfully
    registerLineBinding()
  }

  // get the language from path, and set the path language in GATSBY_I18NEXT_KEY
  // in other words, the language in path has more precedence over the language in browser settings
  const pathLanguage = getLanguageFromLocation()
  if (pathLanguage) {
    localStorage.setItem(GATSBY_I18NEXT_KEY, pathLanguage)
  }

  // need to backup the original language, before anything happens
  // this is especially for auth callback page
  // because that page only has the default language page
  // we need to be able to get the language before redirecting to callback url
  const i18nextLanguage = normalizeLanguage(
    localStorage.getItem(GATSBY_I18NEXT_KEY),
  )
  if (i18nextLanguage) {
    localStorage.setItem(GATSBY_I18NEXT_KEY, i18nextLanguage)
    localStorage.setItem(BACKUP_GATSBY_I18NEXT_KEY, i18nextLanguage)
  }

  const awsConfigUpdatedForLanguage = {
    ...awsConfig,
    Auth: {
      ...awsConfig.Auth,
      oauth: {
        ...awsConfig.Auth.oauth,
        redirectSignOut: rewritePathWithLanguage(
          awsConfig.Auth.oauth.redirectSignOut,
          i18nextLanguage,
        ),
      },
    },
  }

  if (process.env.GATSBY_AMPLIFY_DEBUG) {
    Logger.LOG_LEVEL = "DEBUG"
  }

  // If the user comes from third-party client site, update the AWS config with the third-party client info
  if (hasCallbackClient()) {
    const {id} = getCallbackClient()
    awsConfigUpdatedForLanguage.Auth.userPoolWebClientId = id
  }

  if (error && errorDescription) {
    // clear the session storage
    clearAmplifyOAuthState()

    // If Oauth error is present, redirect to login page with error message
    const errorResponse = decodeURIComponent(errorDescription).match(/\{.*\}/)
    if (errorResponse) {
      const {
        error: {code},
      } = JSON.parse(errorResponse[0])
      window.location.href = rewritePathWithLanguage(
        `/auth/sign-in?oauthError=${code}`,
      )
      return
    } else {
      // If error is not in the expected format, redirect to login page with no error message
      window.location.href = rewritePathWithLanguage(`/auth/sign-in`)
      return
    }
  }

  if (liffInit) {
    // according to LIFF docs, https://developers.line.biz/en/docs/liff/opening-liff-app/#redirect-flow
    // when user access this url https://liff.line.me/{our liff id}/auth/sign-in?line_bind=true"
    // the liff server would first take user to this url: {Endpoint URL specified in liff console}/?liff.state=${urlencode(/auth/sign-in?line_bind=true)}
    // we need to call liff.init and wait for it to finish
    // however, we are not able to use async/await here, so, only register the line binding,
    // and do it in the index page (the endpoint URL)
    registerLiffInit()
  } else {
    if (idToken && refreshToken && accessToken) {
      injectCognitoToken(idToken, accessToken, refreshToken)
    }

    // https://github.com/aws-amplify/amplify-js/blob/aws-amplify%405.0.7/packages/auth/src/Auth.ts
    // in aws auth, it would listen to url changed, and handle the tokens
    // however, in the in-app browser of LINE, it also needs the tokens to do the init
    // hence, let's do a HACK here, only do AWS configure if liffInit(liff.state) is not present
    Amplify.configure(awsConfigUpdatedForLanguage)
  }

  // print build version under console
  if (process.env.BUILD_VERSION) {
    console.log("BUILD VERSION: ", process.env.BUILD_VERSION)
  } else {
    console.log("BUILD VERSION: ", BUILD_VERSION)
  }
}

export const wrapRootElement = ({element}) => {
  return (
    <RecaptchaProviders>
      <QueryProvider>{element}</QueryProvider>
    </RecaptchaProviders>
  )
}

/** @type {import('gatsby').GatsbyBrowser['wrapPageElement']} */
export const wrapPageElement = ({element}, options) => {
  const newElement = React.cloneElement(
    element, // I18nextProvider
    element.props,
    element.props.children
      ? React.cloneElement(
          element.props.children, // I18nextContext.Provider
          element.props.children.props,
          React.createElement(
            RootPageElement,
            undefined,
            element.props.children.props.children,
          ),
        )
      : null,
  )
  return newElement
}
